import React, { createContext, useContext, useCallback, useReducer, useMemo } from 'react';

const SelectContext = createContext({});
function registerOption(dipatch, option) {
    dipatch({ type: 'REGISTER_OPTION', option });
}
function selectOption(dipatch, option) {
    dipatch({ type: 'SELECT_OPTION', option });
}
function unselectOption(dipatch, option) {
    dipatch({ type: 'UNSELECT_OPTION', option });
}
function searchOptions(dipatch, searchedTerm, minSearchChars = 0) {
    dipatch({ type: 'FILTER_OPTIONS', searchedTerm, minSearchChars });
}
function clearFilteredOptions(dipatch) {
    dipatch({ type: 'CLEAR_FILTERED_OPTIONS' });
}
function setPreviousSelectedOptions(dipatch) {
    dipatch({ type: 'SET_PREVIOUS_SELECTED_OPTIONS' });
}
function unselectAllOptions(dipatch) {
    dipatch({ type: 'UNSELECT_ALL_OPTIONS' });
}
const normalizeString = (text) => text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
function optionsReducer(state, action) {
    var _a, _b, _c, _d, _e;
    switch (action.type) {
        case 'REGISTER_OPTION': {
            if (!((_a = action.option) === null || _a === void 0 ? void 0 : _a.value)) {
                return state;
            }
            const initialSelectedOptions = state.options.filter((option) => option.selected);
            if (state.options.some((option) => { var _a; return option.value === ((_a = action.option) === null || _a === void 0 ? void 0 : _a.value); })) {
                const updatedSimilarOptions = state.options.map((option) => { var _a; return option.value === ((_a = action.option) === null || _a === void 0 ? void 0 : _a.value) ? action.option : option; });
                return Object.assign(Object.assign({}, state), { options: updatedSimilarOptions, selectedOptions: initialSelectedOptions });
            }
            return Object.assign(Object.assign({}, state), { options: [...state.options, action.option], selectedOptions: initialSelectedOptions });
        }
        case 'SELECT_OPTION': {
            const selectOption = (option) => {
                var _a;
                return option.value === ((_a = action.option) === null || _a === void 0 ? void 0 : _a.value)
                    ? Object.assign(Object.assign({}, option), { selected: true }) : state.multi
                    ? option
                    : Object.assign(Object.assign({}, option), { selected: false });
            };
            const updatedOptions = state.options.map(selectOption);
            const selectedOptions = updatedOptions.filter((option) => option.selected);
            return Object.assign(Object.assign({}, state), { options: updatedOptions, selectedOptions, filteredOptions: state.filteredOptions.map(selectOption), previousSelectedOptions: (_b = state.previousSelectedOptions) === null || _b === void 0 ? void 0 : _b.map(selectOption) });
        }
        case 'UNSELECT_OPTION': {
            const unselectOption = (option) => {
                var _a;
                return option.value === ((_a = action.option) === null || _a === void 0 ? void 0 : _a.value)
                    ? Object.assign(Object.assign({}, option), { selected: false }) : option;
            };
            const currentSelectedOptions = state.options.map(unselectOption);
            const selectedOptions = currentSelectedOptions.filter((option) => option.selected);
            return Object.assign(Object.assign({}, state), { options: currentSelectedOptions, selectedOptions, filteredOptions: state.filteredOptions.map(unselectOption), previousSelectedOptions: (_c = state.previousSelectedOptions) === null || _c === void 0 ? void 0 : _c.map(unselectOption) });
        }
        case 'UNSELECT_ALL_OPTIONS': {
            const unselectAllOptions = (option) => (Object.assign(Object.assign({}, option), { selected: false }));
            return Object.assign(Object.assign({}, state), { options: state.options.map(unselectAllOptions), selectedOptions: [], filteredOptions: state.filteredOptions.map(unselectAllOptions), previousSelectedOptions: (_d = state.previousSelectedOptions) === null || _d === void 0 ? void 0 : _d.map(unselectAllOptions) });
        }
        case 'CLEAR_FILTERED_OPTIONS': {
            return Object.assign(Object.assign({}, state), { isSearched: false, searchedTerm: '', filteredOptions: [] });
        }
        case 'FILTER_OPTIONS': {
            if (!action.searchedTerm) {
                return Object.assign(Object.assign({}, state), { isSearched: false, searchedTerm: '' });
            }
            if (action.searchedTerm.length < ((_e = action === null || action === void 0 ? void 0 : action.minSearchChars) !== null && _e !== void 0 ? _e : 0)) {
                return Object.assign(Object.assign({}, state), { isSearched: false, searchedTerm: action.searchedTerm });
            }
            const normalizedSearchTerm = normalizeString(action.searchedTerm);
            const filteredOptions = state.options.filter((option) => {
                const normalizedText = normalizeString(option.text);
                return normalizedText.includes(normalizedSearchTerm);
            });
            return Object.assign(Object.assign({}, state), { isSearched: true, searchedTerm: action.searchedTerm, filteredOptions });
        }
        case 'SET_PREVIOUS_SELECTED_OPTIONS': {
            const previousSelectedOptions = state.selectedOptions;
            return Object.assign(Object.assign({}, state), { previousSelectedOptions });
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`);
        }
    }
}
function SelectProvider({ children, multi, onSelect, onClose, isMobile, minSearchChars = 0, }) {
    const safeOnSelect = useCallback(onSelect, [onSelect]);
    const [state, dispatch] = useReducer(optionsReducer, {
        isMobile,
        multi,
        onSelect: safeOnSelect,
        isSearched: false,
        searchedTerm: '',
        options: [],
        selectedOptions: [],
        filteredOptions: [],
        previousSelectedOptions: [],
        onClose,
        minSearchChars,
    });
    const value = useMemo(() => [state, dispatch], [state]);
    return (React.createElement(SelectContext.Provider, { value: value }, children));
}
function useSelect() {
    const context = useContext(SelectContext);
    if (context === undefined) {
        throw new Error('useSelect must be used within a SelectProvider');
    }
    return context;
}

export { SelectProvider, clearFilteredOptions, registerOption, searchOptions, selectOption, setPreviousSelectedOptions, unselectAllOptions, unselectOption, useSelect };
